import { PulseLoader } from "@/components/pulse_loader";
import { useMediaQuery } from "@/hooks";
import { colors } from "@/offering_v2/components/colors";
import classNames from "classnames";
import pluralize from "pluralize";
import React, { useState } from "react";
import { BsInfoCircleFill } from "react-icons/bs";
import { useParams } from "react-router-dom";
import { useCreatePaymentIntent } from "../hooks/use_create_payment_intent";
import { PortalPathParams } from "../hooks/use_portal_history";
import { useSubscriptionDetailsQuery } from "../hooks/use_subscription_details_query";
import { useSubscriptionPolicyQuery } from "../hooks/use_subscription_policy_query";
import { PortalChangePaymentMethodModal } from "./portal_change_payment_method_modal";

interface MembershipFailedPaymentBarProps {
  partnerId: string;
  partnerSlug: string;
  partnerName: string;
}

export function MembershipFailedPaymentBar({
  partnerId,
  partnerSlug,
  partnerName,
}: MembershipFailedPaymentBarProps) {
  const { device } = useMediaQuery();
  const { subscriptionId } = useParams<PortalPathParams>();

  const [openDialog, setOpenDialog] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const [loadingClientSecret, setLoadingClientSecret] = useState(false);

  const createPaymentIntent = useCreatePaymentIntent();
  const {
    revalidate: revalidateSubscriptionDetails,
    data: subscriptionDetailsData,
  } = useSubscriptionDetailsQuery(partnerId, subscriptionId);
  const { data: policyData } = useSubscriptionPolicyQuery(
    subscriptionDetailsData?.membership.membershipPlanId
  );

  const handleClickChangePaymentMethod = async () => {
    setLoadingClientSecret(true);
    const response = await createPaymentIntent(partnerId, subscriptionId);
    const clientSecret = response?.data.clientSecret;
    if (clientSecret) {
      setOpenDialog(true);
      setClientSecret(clientSecret);
    }
    setLoadingClientSecret(false);
  };

  const handleOnSucceed = () => {
    setOpenDialog(false);
    revalidateSubscriptionDetails();
  };

  return (
    <div>
      <div
        className={classNames(
          device !== "mobile" && "d-flex gap-3 align-items-center",
          "p-4 rounded"
        )}
        style={{
          background: colors.grapefruitMinus70,
          color: colors.grapefruit60,
        }}
      >
        {device !== "mobile" && (
          <div>
            <BsInfoCircleFill size={32} />
          </div>
        )}
        <div>
          <b>Your recent membership payment failed.</b> Please update your
          payment method within{" "}
          {policyData?.cancellationDaysSincePaymentFailure}{" "}
          {pluralize("day", policyData?.cancellationDaysSincePaymentFailure)} to
          avoid your membership from automatically cancelling.{" "}
          {loadingClientSecret ? (
            <PulseLoader />
          ) : (
            <u role="button" onClick={handleClickChangePaymentMethod}>
              Update payment method
            </u>
          )}
        </div>
      </div>

      {clientSecret && (
        <PortalChangePaymentMethodModal
          show={openDialog}
          onHide={() => setOpenDialog(false)}
          onSucceed={handleOnSucceed}
          clientSecret={clientSecret}
          partnerId={partnerId}
          partnerSlug={partnerSlug}
          subscriptionId={subscriptionId}
        />
      )}
    </div>
  );
}
